import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectedOrganizationSelector } from '../../redux/selector';
import { ROWS_PER_PAGE_OPTIONS } from './components/MessageTable/MessageTable';

const SEARCH_DEBOUNCE_MS = 400;
const DEFAULT_STATUSES = ['sent', 'scheduled'];
// At most this many messages (one a week for a year) can be selected at once.
// The search and the date range only filter the table and have no limit.
// andromeda's analytics-v2 routes accept as many ids.
export const MAX_SELECTED = 52;

// The date range as the list endpoint takes it: unix seconds from the start
// of the first day to the end of the last.
export const getRangeParams = range => ({
  start: range[0].clone().startOf('day').unix(),
  end: range[1].clone().endOf('day').unix(),
});

const TableStateContext = createContext(null);

// The message table's search, date range, filter, paging and selection.
// The provider sits above the table and detail routes, so the table shows the
// same view when the user comes back from a message's detail page.
export const TableStateProvider = ({ children }) => {
  const orgId = useSelector(selectedOrganizationSelector).id;

  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [statuses, setStatuses] = useState(DEFAULT_STATUSES);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  // Selected messages by id. The row is kept so the graph can use selected
  // messages that are not on the current page.
  const [selected, setSelected] = useState(new Map());
  // "Show selected messages only": the table lists only the selected messages,
  // and search, the date range and Filter are not applied.
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const selectedIds = showSelectedOnly ? [...selected.keys()].join(',') : '';

  useEffect(() => {
    const timeout = setTimeout(
      () => setSearch(searchText.trim()),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timeout);
  }, [searchText]);

  useEffect(() => {
    setPage(0);
  }, [search, dateRange, statuses, rowsPerPage, showSelectedOnly, selectedIds]);

  // The toggle is only shown while something is selected, so it turns off
  // when the selection is cleared.
  useEffect(() => {
    if (selected.size === 0) setShowSelectedOnly(false);
  }, [selected]);

  useEffect(() => {
    setSelected(new Map());
    setPage(0);
  }, [orgId]);

  const value = {
    orgId,
    searchText,
    setSearchText,
    search,
    dateRange,
    setDateRange,
    statuses,
    setStatuses,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    selected,
    setSelected,
    showSelectedOnly,
    setShowSelectedOnly,
    selectedIds,
  };

  return (
    <TableStateContext.Provider value={value}>
      {children}
    </TableStateContext.Provider>
  );
};

export const useTableState = () => useContext(TableStateContext);
